import { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Divider,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HistoryIcon from "@mui/icons-material/History";
import DescriptionIcon from "@mui/icons-material/Description";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import MedicationIcon from "@mui/icons-material/Medication";
import BiotechIcon from "@mui/icons-material/Biotech";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import EventNoteIcon from "@mui/icons-material/EventNote";
import VitalsPanel from "./VitalsPanel";
import { riskColor } from "../../../nurse-dashboard/theme";
import ClinicalNotesModal from "../../components/modals/ClinicalNotesModal";
import MonitoringModal from "../../components/modals/MonitoringModal";
import MedicationModal from "../../components/modals/MedicationModal";
import TestModal from "../../components/modals/TestModal";
import DietModal from "../../components/modals/DietModal";
import DischargeAdviceModal from "../../components/modals/DischargeAdviceModal";
import DischargeStageTracker from "../../components/DischargeStageTracker";
import CancelDischargeModal from "../../components/modals/CancelDischargeModal";
import { fetchDischargeProcess } from "../../api/dischargeProcessApi";
import PatientTimelineModal from "../../components/modals/PatientTimelineModal";
import TaskPlansModal from "../../components/modals/TaskPlansModal";
import VoiceAssistantModal from "../../components/modals/VoiceAssistantModal";
import DischargeSummaryModal from "../../components/modals/DischargeSummaryModal";
import OtRequestSection from "../../../doctor-console/Sections/OtRequestSection";
import ViewDischargeDraftModal from "../../components/modals/ViewDischargeDraftModal";
import CarePlanWizard from "../../components/modals/CarePlanWizard";
import CarePlanActivityModal from "../../components/modals/CarePlanActivityModal";
import MedicationOrdersPanel from "./MedicationOrdersPanel";
import Patient360Modal from "../../../doctor-console/Utils/Patient360Modal";
import OrderManagementDrawer from "./OrderManagementDrawer";
import { normalizeVoiceCarePlanData } from "../../utils/voiceCarePlanNormalize";
import {
  postCarePlanBillingCharges,
  stripBillingMetaFromPayload,
  stripServiceMetaDeep,
} from "../../utils/carePlanBilling";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MicIcon from "@mui/icons-material/Mic";
import VideocamIcon from "@mui/icons-material/Videocam";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useIpdData } from "../../context/IpdDataContext";
import axios from "axios";
import { toast } from "react-toastify";
import { formatIstDateTime } from "../../../../utils/istDateTime";

const PanelContainer = styled(Paper)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  borderRadius: 8,
  overflow: "hidden",
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: "#ffffff",
}));

const REFERRAL_IN_IPD_ROLE = "referral_in_ipd";

const CarePlanButton = styled(Button)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(0.5),
  padding: theme.spacing(1.5),
  minHeight: 70,
  "& .MuiSvgIcon-root": {
    fontSize: 22,
  },
  color: "#fff",
  backgroundColor: "#0E3F66",
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const isTelemedicineStructure = (structure) => {
  const name = String(structure?.name || "").toLowerCase();
  return (
    name.includes("telemedicine") ||
    name.includes("tele medicine") ||
    name === "tele"
  );
};

// nurse_dashboard/vitals/ record -> shape expected by VitalsPanel
const mapVitalsRecord = (record) => {
  if (!record) return null;
  return {
    bloodPressure: record.blood_pressure || "--/--",
    heartRate: record.heart_rate ?? "--",
    temperature: record.temperature ?? "--",
    oxygenSaturation: record.oxygen_saturation ?? "--",
    respiratoryRate: record.respiratory_rate ?? "--",
    weight: record.weight ?? "--",
    // recorded_at is a UTC instant that arrives unmarked; new Date() read it
    // as local and showed vitals 5h30m before they were taken.
    lastUpdated: formatIstDateTime(record.recorded_at, "-"),
  };
};

const defaultRecurrence = {
  isRecurring: false,
  frequency: "daily",
  interval: 1,
  specificTimes: [],
  daysOfWeek: [],
  dayOfMonth: null,
  startDateTime: "",
  endCondition: {
    type: "never",
    count: null,
    untilDateTime: null,
    duration: { value: null, unit: "days" },
  },
};

// Map care plan type to task category
const mapTypeToCategory = (type) => {
  const categoryMap = {
    medication: "Medicine",
    monitoring: "Vitals",
    test: "Labs",
    "clinical-notes": "Rounds",
    diet: "Rounds",
    discharge: "Rounds",
  };
  return categoryMap[type] || "";
};

// Build one task from a single list item (monitoring / test / medication) when item is tagged
const carePlanItemToTask = (type, item) => {
  const id =
    crypto.randomUUID?.() ||
    `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const now = new Date().toISOString();
  const base = {
    id,
    priority: "Medium",
    dueDate: "",
    dueTime: "",
    assignedTo: "Nurse",
    category: mapTypeToCategory(type),
    status: "Pending",
    createdAt: now,
    recurrencePattern: defaultRecurrence,
  };
  if (type === "monitoring")
    return {
      ...base,
      taskTitle: `Monitoring: ${item.parameter}`,
      description:
        `${item.frequency}${item.thresholds ? ` • ${item.thresholds}` : ""}${item.responsibleRole ? ` • ${item.responsibleRole}` : ""}`.trim() ||
        "",
    };
  if (type === "medication")
    return {
      ...base,
      taskTitle: `Medication: ${item.drugName} ${item.dose}`,
      description: `${item.route} • ${item.frequency}`.trim() || "",
    };
  if (type === "test")
    return {
      ...base,
      taskTitle: `Test: ${item.testName}`,
      description:
        `${item.priority}${item.clinicalIndication ? ` • ${item.clinicalIndication}` : ""}`.trim() ||
        "",
    };
  return {
    ...base,
    taskTitle: type,
    description: JSON.stringify(item).slice(0, 200),
  };
};

// Build a task from a care plan submission (single-form modals: clinical-notes, diet, discharge)
const carePlanToTask = (type, data) => {
  const id =
    crypto.randomUUID?.() ||
    `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const now = new Date().toISOString();
  const base = {
    id,
    priority: "Medium",
    dueDate: "",
    dueTime: "",
    assignedTo: "Nurse",
    category: mapTypeToCategory(type),
    status: "Pending",
    createdAt: now,
    recurrencePattern: defaultRecurrence,
  };
  switch (type) {
    case "clinical-notes":
      return {
        ...base,
        taskTitle: data.condition
          ? `Clinical notes: ${data.condition}`
          : "Clinical notes",
        description: [data.goals, data.notes].filter(Boolean).join("\n") || "",
      };
    case "diet":
      return {
        ...base,
        taskTitle: data.dietType ? `Diet: ${data.dietType}` : "Diet plan",
        description:
          [data.restrictions, data.feedingMode, data.dieticianReferral]
            .filter(Boolean)
            .join(" • ") || "",
      };
    case "discharge":
      return {
        ...base,
        taskTitle: "Discharge plan",
        description:
          [
            data.dischargePlan,
            data.tentativeDate && `Target: ${data.tentativeDate}`,
            data.followUpInstructions,
          ]
            .filter(Boolean)
            .join("\n") || "",
      };
    default:
      return {
        ...base,
        taskTitle: type,
        description: JSON.stringify(data).slice(0, 200),
      };
  }
};

// Returns array of tasks for task list (one per tagged item for list modals, or single task for form modals)
const getTasksFromCarePlan = (type, data, options) => {
  if (!options?.addToTaskList) return [];
  if (type === "monitoring") {
    const items = (data.monitoringItems || []).filter((i) => i.addToTaskList);
    return items.map((item) => carePlanItemToTask("monitoring", item));
  }
  if (type === "medication") {
    const items = (data.medications || []).filter((m) => m.addToTaskList);
    return items.map((item) => carePlanItemToTask("medication", item));
  }
  if (type === "test") {
    const items = (data.tests || []).filter((t) => t.addToTaskList);
    return items.map((item) => carePlanItemToTask("test", item));
  }
  return [carePlanToTask(type, data)];
};

/**
 * GET care_plans often returns FHIR-style nested data.medication.{ name, frequency, ... }.
 * Dynamic activity forms usually bind medicine name as root `name` or `drugName`, so the API
 * never receives an update to medication.name and keeps old (e.g. voice) values. Merge root
 * fields into data.medication before POST.
 */
const mergeMedicationActivityPayloadForApi = (data) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return data;
  const out = { ...data };
  const prev =
    out.medication && typeof out.medication === "object"
      ? { ...out.medication }
      : {};
  const preferredName = [
    out.drugName,
    out.name,
    out.medicineName,
    out.medicine_name,
  ]
    .map((x) => (x != null && String(x).trim() !== "" ? String(x).trim() : ""))
    .find(Boolean);
  const name = (preferredName || String(prev.name || "").trim()).trim();

  const medKeys = [
    "frequency",
    "start",
    "end",
    "period",
    "periodUnit",
    "safety",
  ];
  const merged = { ...prev };
  medKeys.forEach((k) => {
    const root = out[k];
    if (root !== undefined && root !== null && String(root).trim() !== "") {
      merged[k] = root;
    }
  });
  if (name) merged.name = name;

  out.medication = merged;
  if (out.drugName !== undefined) delete out.drugName;
  const rootName = out.name;
  if (rootName != null && String(rootName).trim() === name && name) {
    delete out.name;
  }
  return out;
};

const isMedicationCarePlanStructure = (structure) => {
  const n = String(structure?.name || "").toLowerCase();
  return n.includes("medication") || n.includes("medicine") || n === "med";
};

const asArray = (value) =>
  Array.isArray(value) ? value : value ? [value] : [];

const firstNonEmpty = (...values) =>
  values.find(
    (value) =>
      value !== undefined && value !== null && String(value).trim() !== "",
  ) || "";

const extractScheduledDateTime = (entry = {}) =>
  firstNonEmpty(
    entry.scheduled_datetime,
    entry.scheduledDateTime,
    entry.occurrenceDateTime,
    entry.datetime,
    entry.dateTime,
    entry.start_datetime,
    entry.startDateTime,
    entry.start,
  );

const buildCompactDetail = (activityName, entry = {}) => {
  const headline =
    firstNonEmpty(
      entry.task,
      entry.taskName,
      entry.title,
      entry.name,
      entry.testName,
    ) || activityName;
  const details = [];
  const frequency = firstNonEmpty(
    entry.frequency,
    entry?.medication?.frequency,
  );
  const priority = firstNonEmpty(entry.priority);
  const notes = firstNonEmpty(
    entry.notes,
    entry.description,
    entry.reason,
    entry.instruction,
  );
  if (frequency) details.push(`Frequency: ${frequency}`);
  if (priority) details.push(`Priority: ${priority}`);
  if (notes) details.push(notes);
  return [headline, ...details].join(" | ");
};

const getInitials = (name) => {
  if (!name) return "?";
  return name
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();
};

// Client-side, UX-only check for "is the current user the admitting doctor".
// The backend (`doctor_has_access` / `transfer_admitting`) is the real
// authorization boundary — this only decides whether to show the Handover
// Patient action; fail-open (show) whenever admittingDoctorId isn't known
// yet, mirroring CancelDischargeModal.js / DischargeSummaryModal.js's
// identical helper (Task E6c).
const isCurrentDoctorAdmitting = (admittingDoctorId) => {
  if (!admittingDoctorId) return true;
  const currentDoctorId = sessionStorage.getItem("doctor_id");
  if (!currentDoctorId) return true;
  return String(currentDoctorId) === String(admittingDoctorId);
};

// Holds the patient id Order Management is open for, so a reload can restore it. Paired
// with the console's own ipdDoctorSelectedPatientId — both must point at the same patient
// for the screen to come back.
export const ORDER_MGMT_OPEN_KEY = "ipdDoctorOrderMgmtOpenFor";

const PatientDetailsPanel = ({
  patient,
  onAddCarePlan,
  onStopMedication,
  onClose,
}) => {
  const history = useHistory();
  const {
    getTasks,
    saveTasks,
    addCarePlan,
    addReferredDoctor,
    removeReferredDoctor,
    getAvailableDoctors,
  } = useIpdData();
  // const { getTasks, saveTasks, addCarePlan } = useIpdData();
  const [activeModal, setActiveModal] = useState(null);
  // Order Management is a full-screen view with no route of its own, so a reload used to
  // land the doctor back on the patient list. Remembering *which patient* it was open for
  // (not just a boolean) means it only reopens when the console restores that same
  // patient — a stale flag can never reopen it over somebody else's chart.
  const [orderMgmtOpen, setOrderMgmtOpen] = useState(() => {
    try {
      return sessionStorage.getItem(ORDER_MGMT_OPEN_KEY) === String(patient?.id ?? "");
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      if (orderMgmtOpen && patient?.id != null) {
        sessionStorage.setItem(ORDER_MGMT_OPEN_KEY, String(patient.id));
      } else {
        sessionStorage.removeItem(ORDER_MGMT_OPEN_KEY);
      }
    } catch {}
  }, [orderMgmtOpen, patient?.id]);

  const [taskPlansByPatient, setTaskPlansByPatient] = useState({});
  const [wizardData, setWizardData] = useState(null);
  const [carePlanStructures, setCarePlanStructures] = useState([]);
  const [loadingCarePlanStructures, setLoadingCarePlanStructures] =
    useState(false);
  const [selectedActivityStructure, setSelectedActivityStructure] =
    useState(null);
  const [patientSummary, setPatientSummary] = useState({});
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [careContextList, setCareContextList] = useState([]);
  const [selectedContextForRetrieveFhir, setSelectedContextForRetrieveFhir] =
    useState([]);
  const [s3BucketFhir, setS3BucketFhir] = useState(null);
  const [abdmData] = useState([]);
  const [fetchedVitals, setFetchedVitals] = useState(null);
  // Latest nurse risk assessment (nursing-ops-2 #14) — display-only; risk stays
  // nurse-owned, so this panel never writes it.
  const [riskAssessment, setRiskAssessment] = useState(null);
  // Transfer Care (nursing-ops-2 #09): propose a new attending; the receiving
  // doctor accepts from their Referred To Me inbox before anything flips.
  const [transferCareOpen, setTransferCareOpen] = useState(false);
  const [transferDoctors, setTransferDoctors] = useState([]);
  const [transferTarget, setTransferTarget] = useState(null);
  const [transferNote, setTransferNote] = useState("");
  const [transferBusy, setTransferBusy] = useState(false);

  const openTransferCare = async () => {
    setTransferCareOpen(true);
    setTransferTarget(null);
    setTransferNote("");
    try {
      // ?scope=facility is REQUIRED here. Without it the endpoint returns only the
      // calling doctor when the caller is a doctor — and this dialog always runs in the
      // doctor console, so the picker contained exactly one name: your own, making the
      // handover impossible to perform.
      const res = await axios.get(
        `${REACT_APP_BACKEND_ROUTE_URL}get_all_doctors/?scope=facility`,
        { headers }
      );
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      // Handing a patient to yourself is not a handover. Read straight from
      // sessionStorage — the module-level currentDoctorId is out of scope here, and a
      // blank value simply means nothing is filtered rather than everything.
      const me = sessionStorage.getItem("doctor_id") || "";
      setTransferDoctors(
        me ? list.filter((d) => String(d.doctor_id || d.id) !== String(me)) : list
      );
    } catch (error) {
      console.error("Error loading doctors for transfer care:", error);
      setTransferDoctors([]);
    }
  };

  const submitTransferCare = async () => {
    if (!transferTarget || !resolvedAdmissionId) return;
    setTransferBusy(true);
    try {
      const res = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}ipd_admission/${resolvedAdmissionId}/transfer_care/`,
        {
          to_doctor_id: transferTarget.doctor_id || transferTarget.id,
          note: transferNote.trim() || undefined,
        },
        { headers },
      );
      if (res.data?.status === "Success") {
        toast.success(res.data?.message || "Transfer of care proposed");
        setTransferCareOpen(false);
      } else {
        toast.error(res.data?.message || "Could not propose transfer of care");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not propose transfer of care");
    } finally {
      setTransferBusy(false);
    }
  };
  const [closingCase, setClosingCase] = useState(false);
  const [cleaningBed, setCleaningBed] = useState(null);
  const isReferredToMePatient = patient?.type === "Referral";
  const resolveAdmissionId = (value) => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return 0;
      const direct = Number(trimmed);
      if (Number.isFinite(direct)) return direct;
      const digits = trimmed.match(/\d+/);
      return digits ? Number(digits[0]) : 0;
    }
    return 0;
  };
  const resolvedAdmissionId = resolveAdmissionId(
    patient?.admissionId || patient?.id,
  );
  const [referredDoctors, setReferredDoctors] = useState(
    (patient.referredDoctors || []).filter(
      (doc) => doc.role === REFERRAL_IN_IPD_ROLE,
    ),
  );
  const [addReferredDoctorDialogOpen, setAddReferredDoctorDialogOpen] =
    useState(false);
  const [removeReferredDoctorConfirmOpen, setRemoveReferredDoctorConfirmOpen] =
    useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState("");
  const [referredDoctorToRemove, setReferredDoctorToRemove] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [availableDoctors, setAvailableDoctors] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [loadingDepts, setLoadingDepts] = useState(false);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [addReferralDoctorNotes, setAddReferralDoctorNotes] = useState("");
  // Handover Patient (Epic 6, E6c) — transfers the admitting-doctor role via
  // PATCH ipd_admission/<id>/admitting_doctor/. Doctor picker reuses the same
  // department -> get_all_doctors_based_on_department cascade as "Add
  // Referral Doctor" above, since the backend accepts any existing
  // DoctorRecord as new_admitting_doctor_id (not just doctors already linked
  // to this admission).
  const [admittingDoctorInfo, setAdmittingDoctorInfo] = useState(null);
  const [doctorsRefreshKey, setDoctorsRefreshKey] = useState(0);
  const [handoverDepartments, setHandoverDepartments] = useState([]);
  const [loadingHandoverDepts, setLoadingHandoverDepts] = useState(false);
  const [handoverDepartment, setHandoverDepartment] = useState("");
  const [handoverDoctors, setHandoverDoctors] = useState([]);
  const [loadingHandoverDoctors, setLoadingHandoverDoctors] = useState(false);
  const [handoverDoctorId, setHandoverDoctorId] = useState("");
  const [submittingHandover, setSubmittingHandover] = useState(false);
  // Discharge workflow follows the real DischargeProcess row (created by the
  // doctor's Discharge Advice submission, Task C11) — fetched below.
  // undefined = loading, null = confirmed no active process
  const [dischargeProcess, setDischargeProcess] = useState(undefined);
  const dischargeInitiated = Boolean(
    dischargeProcess?.stage && dischargeProcess.stage !== "cancelled",
  );
  const dischargeWorkflowActive = dischargeInitiated;
  const dischargeOnlyActionsDisabled = dischargeWorkflowActive;
  const dischargeSummaryDisabled = !dischargeWorkflowActive;

  // Nursing Notes (Epic 9, D11) — read-only view of nurse_dashboard/nursing_notes/
  // for this admission, shown in the doctor console. `nursingNotesError` covers
  // both network errors and the 200-Failed envelope (status !== "Success"); it
  // is never treated as data.
  const [nursingNotes, setNursingNotes] = useState([]);
  const [nursingNotesLoading, setNursingNotesLoading] = useState(false);
  const [nursingNotesError, setNursingNotesError] = useState(false);

  useEffect(() => {
    if (!resolvedAdmissionId) {
      setDischargeProcess(null);
      return;
    }
    setDischargeProcess(undefined);
    let isMounted = true;
    fetchDischargeProcess(resolvedAdmissionId)
      .then((res) => {
        if (isMounted) setDischargeProcess(res?.data || null);
      })
      .catch(() => {
        if (isMounted) setDischargeProcess(null);
      });
    return () => {
      isMounted = false;
    };
  }, [resolvedAdmissionId]);

  // Reset synchronously on every admission switch (same rationale as the
  // admitting-doctor gate below, Task E6c): without this, Patient A's notes
  // would linger on screen while Patient B's fetch is in flight or if it fails.
  useEffect(() => {
    setNursingNotes([]);
    setNursingNotesError(false);
    if (!resolvedAdmissionId) {
      setNursingNotesLoading(false);
      return;
    }
    setNursingNotesLoading(true);
    let isMounted = true;
    const BASE = process.env.REACT_APP_BACKEND_ROUTE_URL;
    const token = sessionStorage.getItem("accessToken") || "";
    axios
      .get(`${BASE}nurse_dashboard/nursing_notes/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
          "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
          "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
          USERNAME: sessionStorage.getItem("userName") || "",
        },
        params: { admission_id: resolvedAdmissionId },
      })
      .then((res) => {
        if (!isMounted) return;
        if (res?.data?.status === "Success") {
          const list = Array.isArray(res.data?.data) ? res.data.data : [];
          setNursingNotes(
            [...list].sort(
              (a, b) =>
                new Date(b?.recorded_at || 0) - new Date(a?.recorded_at || 0),
            ),
          );
        } else {
          // 200-Failed envelope — not an error, but not data either.
          setNursingNotesError(true);
        }
      })
      .catch((err) => {
        console.error(
          `Error loading nursing notes for admission ${resolvedAdmissionId}:`,
          err?.response?.data || err.message,
        );
        if (isMounted) setNursingNotesError(true);
      })
      .finally(() => {
        if (isMounted) setNursingNotesLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [resolvedAdmissionId]);

  useEffect(() => {
    if (patient?.type === "Referral") {
      setActiveModal((modal) =>
        modal === "discharge-summary" || modal === "view-discharge-draft"
          ? null
          : modal,
      );
      return;
    }

    if (dischargeInitiated) {
      // Discharge in progress: only discharge-summary actions remain available.
      setSelectedActivityStructure(null);
      setAddReferredDoctorDialogOpen(false);
      setRemoveReferredDoctorConfirmOpen(false);
      setActiveModal((modal) =>
        modal === "discharge-summary" || modal === "view-discharge-draft"
          ? modal
          : null,
      );
      return;
    }

    // Discharge not initiated: close discharge-only modals.
    setActiveModal((modal) =>
      modal === "discharge-summary" || modal === "view-discharge-draft"
        ? null
        : modal,
    );
  }, [patient.id, patient?.type, dischargeInitiated]);

  useEffect(() => {
    let isMounted = true;
    // Clear the admitting-doctor gate synchronously on every patient switch,
    // BEFORE the async fetch below. Without this the Handover button would keep
    // showing the previous patient's admitting doctor until this fetch resolves
    // (or forever if it errors), i.e. offer handover on a patient the doctor
    // isn't admitting. Fail closed: hidden until the fetch confirms.
    setAdmittingDoctorInfo(null);
    setReferredDoctors(
      (patient.referredDoctors || []).filter(
        (doc) => doc.role === REFERRAL_IN_IPD_ROLE,
      ),
    );

    if (isReferredToMePatient || !resolvedAdmissionId) return;
    const BASE = process.env.REACT_APP_BACKEND_ROUTE_URL;
    const token = sessionStorage.getItem("accessToken") || "";
    axios
      .get(`${BASE}ipd_admission/${resolvedAdmissionId}/doctors/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
          "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
          "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
          USERNAME: sessionStorage.getItem("userName") || "",
        },
      })
      .then((res) => {
        if (!isMounted) return;
        const attending = res.data?.doctors?.attending || [];
        setReferredDoctors(
          attending
            .filter((doc) => doc.role === REFERRAL_IN_IPD_ROLE)
            .map((doc) => ({
              id: doc.id,
              name: doc.name,
              specialty: doc.specialty || "",
              initials: getInitials(doc.name || ""),
              role: doc.role,
            })),
        );
        // Admitting doctor (Task E6c) — used to gate/refresh the Handover
        // Patient action after a transfer.
        setAdmittingDoctorInfo(res.data?.doctors?.admitting || null);
      })
      .catch((err) => {
        console.error(
          "[doctors API] error:",
          err?.response?.data || err.message,
        );
        // fallback: keep patient.referredDoctors already set above
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient.id, doctorsRefreshKey]);

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Close Case: the admitting doctor discharges the case. The close endpoint
  // frees the bed (returning freed_bed_code); we then offer to send it to
  // housekeeping. The API authorizes the admitting-doctor role server-side.
  const handleCloseCase = async () => {
    if (!resolvedAdmissionId) return;
    setClosingCase(true);
    try {
      const BASE = process.env.REACT_APP_BACKEND_ROUTE_URL;
      const token = sessionStorage.getItem("accessToken") || "";
      const res = await axios.post(
        `${BASE}ipd_admission/${resolvedAdmissionId}/close/`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
            "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
            "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
            USERNAME: sessionStorage.getItem("userName") || "",
            "Content-Type": "application/json",
          },
        },
      );
      if (res?.data?.status === "Success") {
        toast.success(res.data.message || "Case closed");
        if (res.data.freed_bed_code) {
          setCleaningBed({
            bedCode: res.data.freed_bed_code,
            admissionId: resolvedAdmissionId,
          });
        }
      } else {
        toast.error(res?.data?.message || "Could not close the case.");
      }
    } catch (err) {
      console.error("Close case failed:", err);
      toast.error(err?.response?.data?.message || "Could not close the case.");
    } finally {
      setClosingCase(false);
    }
  };

  // Handover Patient (Task E6c): transfers the admitting-doctor role via
  // PATCH ipd_admission/<id>/admitting_doctor/. The backend demotes the
  // outgoing (current) admitting doctor to an accepted "attending" link
  // server-side and is the real authorization boundary (only the current
  // admitting doctor may call it). It returns HTTP 200 with
  // {status:"Failed", message} for business errors — same envelope depth
  // (res.data.status) as handleCloseCase above.
  const handleHandoverPatient = async () => {
    if (!handoverDoctorId || !resolvedAdmissionId) return;
    setSubmittingHandover(true);
    try {
      const BASE = process.env.REACT_APP_BACKEND_ROUTE_URL;
      const token = sessionStorage.getItem("accessToken") || "";
      const res = await axios.patch(
        `${BASE}ipd_admission/${resolvedAdmissionId}/admitting_doctor/`,
        { new_admitting_doctor_id: handoverDoctorId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
            "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
            "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
            USERNAME: sessionStorage.getItem("userName") || "",
            "Content-Type": "application/json",
          },
        },
      );
      if (res?.data?.status === "Success") {
        toast.success(res.data.message || "Admitting doctor transferred.");
        setActiveModal(null);
        // Refresh so the demoted (attending) and new admitting doctor show.
        setDoctorsRefreshKey((k) => k + 1);
      } else {
        toast.error(
          res?.data?.message || "Could not transfer the admitting doctor.",
        );
      }
    } catch (err) {
      console.error("Handover patient failed:", err);
      toast.error(
        err?.response?.data?.message ||
        "Could not transfer the admitting doctor.",
      );
    } finally {
      setSubmittingHandover(false);
    }
  };

  // Fetch departments when Handover Patient dialog opens
  useEffect(() => {
    if (activeModal !== "handover-doctor") return;
    setLoadingHandoverDepts(true);
    const BASE = process.env.REACT_APP_BACKEND_ROUTE_URL;
    const token = sessionStorage.getItem("accessToken") || "";
    axios
      .get(`${BASE}get_all_medical_departments/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data?.department_list) {
          setHandoverDepartments(res.data.department_list.map((d) => d.department));
        }
      })
      .catch(() => setHandoverDepartments([]))
      .finally(() => setLoadingHandoverDepts(false));
  }, [activeModal]);

  const handleHandoverDepartmentChange = async (dept) => {
    setHandoverDepartment(dept);
    setHandoverDoctorId("");
    if (!dept || dept === "Other/External") {
      setHandoverDoctors([]);
      return;
    }
    setLoadingHandoverDoctors(true);
    const result = await getAvailableDoctors(dept);
    setHandoverDoctors(result.data || []);
    setLoadingHandoverDoctors(false);
  };

  const resetHandoverForm = () => {
    setHandoverDepartment("");
    setHandoverDoctors([]);
    setHandoverDoctorId("");
  };

  // Fetch departments when Add Referral Doctor dialog opens
  useEffect(() => {
    if (!addReferredDoctorDialogOpen) return;
    setLoadingDepts(true);
    const BASE = process.env.REACT_APP_BACKEND_ROUTE_URL;
    const token = sessionStorage.getItem("accessToken") || "";
    axios
      .get(`${BASE}get_all_medical_departments/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data?.department_list) {
          setDepartments(res.data.department_list.map((d) => d.department));
        }
      })
      .catch(() => setDepartments([]))
      .finally(() => setLoadingDepts(false));
  }, [addReferredDoctorDialogOpen]);

  const handleDepartmentChange = async (dept) => {
    setSelectedDepartment(dept);
    setSelectedDoctorId("");
    if (!dept || dept === "Other/External") {
      setAvailableDoctors([]);
      return;
    }
    setLoadingDoctors(true);
    const result = await getAvailableDoctors(dept);
    setAvailableDoctors(result.data || []);
    setLoadingDoctors(false);
  };

  const resetAddReferredDoctorForm = () => {
    setSelectedDoctorId("");
    setSelectedDepartment("");
    setAvailableDoctors([]);
    setAddReferralDoctorNotes("");
  };

  const handleAddReferredDoctorDialogClose = () => {
    setAddReferredDoctorDialogOpen(false);
  };

  const handleOpenAddReferredDoctorDialog = () => {
    resetAddReferredDoctorForm();
    setAddReferredDoctorDialogOpen(true);
  };

  const handlePromptRemoveReferredDoctor = (doctor) => {
    setReferredDoctorToRemove(doctor);
    setRemoveReferredDoctorConfirmOpen(true);
  };

  const handleConfirmRemoveReferredDoctor = async () => {
    if (!referredDoctorToRemove) return;
    try {
      const response = await removeReferredDoctor(
        resolvedAdmissionId,
        referredDoctorToRemove.id,
      );
      if (response.status === 200) {
        toast.success(
          `${referredDoctorToRemove.name} has been removed successfully.`,
        );
        setReferredDoctors((prev) =>
          prev.filter((d) => d.id !== referredDoctorToRemove.id),
        );
      } else {
        toast.error("Failed to remove referral doctor.");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while removing the doctor.");
    } finally {
      setRemoveReferredDoctorConfirmOpen(false);
      setReferredDoctorToRemove(null);
    }
  };

  const handleAddReferredDoctor = async () => {
    if (!selectedDoctorId) {
      toast.warning("Please select a doctor to add.");
      return;
    }
    const doctorObj = availableDoctors.find(
      (d) => String(d.doctor_id) === String(selectedDoctorId),
    );
    if (!doctorObj) {
      toast.warning("Could not find the selected doctor. Please select again.");
      return;
    }

    try {
      const doctorData = {
        id: doctorObj.doctor_id,
        name: doctorObj.name,
        specialty: doctorObj.specialty || selectedDepartment || "",
        initials: getInitials(doctorObj.name),
        isPrimary: false,
        notes: addReferralDoctorNotes.trim(),
      };
      const response = await addReferredDoctor(resolvedAdmissionId, doctorData);
      if (response.status === 200) {
        const specialty = doctorObj.specialty || selectedDepartment || "";
        const savedNotes = doctorData.notes;
        setSelectedDoctorId("");
        setAddReferralDoctorNotes("");
        setAddReferredDoctorDialogOpen(false);
        setReferredDoctors((prev) => [
          ...prev,
          {
            id: doctorObj.doctor_id,
            name: doctorObj.name,
            specialty,
            initials: getInitials(doctorObj.name),
            notes: savedNotes,
            role: REFERRAL_IN_IPD_ROLE,
          },
        ]);
        toast.success(`${doctorObj.name} has been added as a referral doctor.`);
      } else {
        toast.error("Failed to add referral doctor.");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while adding the doctor.");
    }
  };

  const patientVitals = fetchedVitals ||
    patient?.vitals || {
    bloodPressure: "--/--",
    heartRate: "--",
    temperature: "--",
    oxygenSaturation: "--",
    respiratoryRate: "--",
    weight: "--",
    lastUpdated: "-",
  };
  const patientCarePlans = Array.isArray(patient?.carePlans)
    ? patient.carePlans
    : [];
  const resolvedPatientId =
    patient?.pat_id ||
    patient?.patient_id ||
    patient?.patientId ||
    patient?.id ||
    "";

  const HIDDEN_CARE_PLAN_NAMES = [
    "medication",
    "nutrition",
    "test",
    "discharge advice",
  ];
  const CARE_PLAN_ORDER = ["clinical notes", "orders"];
  const regularCarePlanStructures = useMemo(() => {
    const filtered = carePlanStructures.filter(
      (activity) =>
        !isTelemedicineStructure(activity) &&
        !HIDDEN_CARE_PLAN_NAMES.includes(
          String(activity?.name || "")
            .toLowerCase()
            .trim(),
        ),
    );
    return [...filtered].sort((a, b) => {
      const aName = String(a?.name || "")
        .toLowerCase()
        .trim();
      const bName = String(b?.name || "")
        .toLowerCase()
        .trim();
      const aIdx = CARE_PLAN_ORDER.indexOf(aName);
      const bIdx = CARE_PLAN_ORDER.indexOf(bName);
      if (aIdx === -1 && bIdx === -1) return 0;
      if (aIdx === -1) return 1;
      if (bIdx === -1) return -1;
      return aIdx - bIdx;
    });
  }, [carePlanStructures]);

  const handleTelemedicineClick = () => {
    history.push("/doctor/tele-console-patient", {
      patientId: resolvedPatientId,
      patientName: patient?.name,
      admissionId: resolvedAdmissionId,
    });
  };

  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const authToken = sessionStorage.getItem("accessToken") || "";
  const isAiEnabledForIpd =
    sessionStorage.getItem("isAiEnabled") !== undefined &&
      sessionStorage.getItem("isAiEnabled") !== null
      ? JSON.parse(sessionStorage.getItem("isAiEnabled"))
      : false;
  const isAbdmEnabledForIpd =
    sessionStorage.getItem("isAbdmEnabled") !== undefined &&
      sessionStorage.getItem("isAbdmEnabled") !== null
      ? JSON.parse(sessionStorage.getItem("isAbdmEnabled"))
      : false;
  const headers = {
    Authorization: `Bearer ${authToken}`,
    "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
    "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
    "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
    USERNAME: sessionStorage.getItem("userName") || "",
  };

  // Latest recorded vitals for this admission (nurse_dashboard/vitals/) — feeds the
  // "Current Vitals" panel below instead of the static placeholder from patient.vitals.
  useEffect(() => {
    if (!resolvedAdmissionId) {
      setFetchedVitals(null);
      setRiskAssessment(null);
      return;
    }
    let isMounted = true;
    axios
      .get(`${REACT_APP_BACKEND_ROUTE_URL}nurse_dashboard/vitals/`, {
        headers,
        params: { admission_id: resolvedAdmissionId, latest_per_admission: 1 },
      })
      .then((res) => {
        if (!isMounted) return;
        const list = Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data?.results)
            ? res.data.results
            : [];
        const latest = [...list].sort(
          (a, b) =>
            new Date(b?.recorded_at || 0) - new Date(a?.recorded_at || 0),
        )[0];
        setFetchedVitals(mapVitalsRecord(latest));
      })
      .catch((error) => {
        console.error(
          `Error loading vitals for admission ${resolvedAdmissionId}:`,
          error?.response?.data || error.message,
        );
        if (isMounted) setFetchedVitals(null);
      });
    // Latest nurse risk assessment, same nurse_dashboard source as the vitals.
    axios
      .get(`${REACT_APP_BACKEND_ROUTE_URL}nurse_dashboard/assessment/`, {
        headers,
        params: { admission_id: resolvedAdmissionId },
      })
      .then((res) => {
        if (!isMounted) return;
        const list = Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data?.results)
            ? res.data.results
            : [];
        const latest = [...list].sort(
          (a, b) =>
            new Date(b?.recorded_at || b?.created_at || 0) -
            new Date(a?.recorded_at || a?.created_at || 0),
        )[0];
        setRiskAssessment(latest || null);
      })
      .catch((error) => {
        console.error(
          `Error loading risk assessment for admission ${resolvedAdmissionId}:`,
          error?.response?.data || error.message,
        );
        if (isMounted) setRiskAssessment(null);
      });
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedAdmissionId]);

  const handleFhirArray = (fhirArray) => {
    setS3BucketFhir(fhirArray);
  };

  const getPatientSummary = async () => {
    if (!resolvedPatientId) return;
    setIsLoadingSummary(true);
    try {
      const response = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}summarize_patient_records/`,
        {
          model_choice: 1,
          max_words: 300,
          patient_id: resolvedPatientId,
        },
        { headers },
      );
      if (response?.data?.status === "Success") {
        setPatientSummary(response?.data?.data || {});
      }
    } catch (error) {
      console.error("Error loading patient summary:", error);
    } finally {
      setIsLoadingSummary(false);
    }
  };

  const getCareContexts = async () => {
    if (!resolvedPatientId) return;
    try {
      const payload = {
        filter_type: "patient_id",
        filter_details: {
          patient_id: resolvedPatientId,
        },
      };
      const response = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}fetch_care_contexts_by_filter/`,
        JSON.stringify(payload),
        { headers },
      );
      if (response?.data?.status !== "Failed") {
        setCareContextList(
          Array.isArray(response?.data?.care_contenxts)
            ? response.data.care_contenxts
            : [],
        );
      }
    } catch (error) {
      console.error("Error loading care contexts:", error);
    }
  };

  useEffect(() => {
    if (activeModal !== "patient-360") return;
    getCareContexts();
    getPatientSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModal, resolvedPatientId]);

  useEffect(() => {
    setPatientSummary({});
    setCareContextList([]);
    setSelectedContextForRetrieveFhir([]);
    setS3BucketFhir(null);
  }, [resolvedPatientId]);

  // Load tasks from API when patient changes
  useEffect(() => {
    if (patient?.id) {
      getTasks(patient.id)
        .then((response) => {
          if (response.status === 200 && response.data) {
            setTaskPlansByPatient((prev) => ({
              ...prev,
              [patient.id]: response.data,
            }));
          }
        })
        .catch((error) => {
          console.error("Error loading tasks:", error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.id]);

  useEffect(() => {
    const fetchCarePlanStructures = async () => {
      try {
        setLoadingCarePlanStructures(true);
        const response = await axios.get(
          `${REACT_APP_BACKEND_ROUTE_URL}care_plan_structures/`,
          { headers },
        );
        const structureList = Array.isArray(response?.data?.data)
          ? response.data.data
          : [];
        setCarePlanStructures(structureList);
      } catch (error) {
        console.error("Error loading care plan structures:", error);
        toast.error("Failed to load care plan activities.");
      } finally {
        setLoadingCarePlanStructures(false);
      }
    };

    fetchCarePlanStructures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Shared helper — resolves a backend care_plan_id from a type name string.
  // Used by both manual modal saves and the voice assistant persist flow.
  const resolveCarePlanStructureIdByType = (carePlanType) => {
    const normalizedType = String(carePlanType || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
    if (!normalizedType) return 0;
    const exact = carePlanStructures.find((item) => {
      const nameKey = String(item?.name || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
      const typeKey = String(item?.type || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
      return nameKey === normalizedType || typeKey === normalizedType;
    });
    if (exact)
      return Number(
        exact?.care_plan_id || exact?.id || exact?.template_id || 0,
      );
    const partial = carePlanStructures.find((item) => {
      const nameKey = String(item?.name || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
      return (
        nameKey.includes(normalizedType) || normalizedType.includes(nameKey)
      );
    });
    return Number(
      partial?.care_plan_id || partial?.id || partial?.template_id || 0,
    );
  };

  const handleAddCarePlan = async (type, data, options) => {
    const newCarePlan = { type, data };

    // Save the whole plan as ONE entry to the backend so it persists after refresh.
    // The full data object (medications[], tests[], etc.) is stored as-is — one plan
    // per submission, matching what the user sees in the timeline.
    const resolvedId = resolveCarePlanStructureIdByType(type);
    if (!resolvedId || !resolvedAdmissionId) {
      console.warn(
        `[CarePlan] No structure ID for type "${type}" — saved locally only.`,
      );
      // Nothing to persist server-side for this type — reflect it locally right away.
      onAddCarePlan(patient.id, newCarePlan);
    } else {
      try {
        const res = await axios.post(
          `${REACT_APP_BACKEND_ROUTE_URL}care_plans/`,
          {
            admission_id: resolvedAdmissionId,
            care_plans: [{ care_plan_id: resolvedId, data }],
          },
          { headers },
        );
        // Backend returns 200 {status:"Failed"} (no is_valid key at all) when the
        // save is rejected — e.g. invalid admission_id, caller isn't a doctor on
        // this admission. Checking only is_valid === false misses that entirely,
        // so the timeline showed a phantom entry (this is the care plan that gates
        // the nurse's Initiate Discharge button). Only reflect the entry once the
        // backend actually confirms it; is_valid is kept as a secondary signal.
        if (res?.data?.status === "Success" && res?.data?.is_valid !== false) {
          onAddCarePlan(patient.id, newCarePlan);
        } else {
          console.error(
            "[CarePlan] API rejected entry:",
            res.data?.message || res.data?.detail,
          );
          toast.error(
            res.data?.message || res.data?.detail || "Failed to save care plan.",
          );
        }
      } catch (err) {
        console.error(
          "[CarePlan] Failed to save care plan to API:",
          err?.response?.data || err?.message,
        );
        toast.error(err?.response?.data?.message || "Failed to save care plan.");
      }
    }

    const tasks = getTasksFromCarePlan(type, data, options);
    if (tasks.length > 0) {
      const updatedTasks = [
        ...(taskPlansByPatient[patient.id] || []),
        ...tasks,
      ];
      setTaskPlansByPatient((prev) => ({
        ...prev,
        [patient.id]: updatedTasks,
      }));
      saveTasks(patient.id, updatedTasks).catch((error) => {
        console.error("Error saving tasks:", error);
      });
    }
  };

  const handleSaveTasks = (tasks) => {
    setTaskPlansByPatient((prev) => ({
      ...prev,
      [patient.id]: tasks,
    }));
    // Save to API
    saveTasks(patient.id, tasks).catch((error) => {
      console.error("Error saving tasks:", error);
    });
  };

  const handleCarePlanActivitySubmit = async (formData) => {
    if (!selectedActivityStructure) return;

    const normalizeDateTime = (value) => {
      if (!value || typeof value !== "string") return value;
      return value.includes("T") ? value : value;
    };

    const normalizePayloadDates = (obj) => {
      if (!obj || typeof obj !== "object") return obj;
      if (Array.isArray(obj))
        return obj.map((item) => normalizePayloadDates(item));
      return Object.keys(obj).reduce((acc, key) => {
        const value = obj[key];
        if (value && typeof value === "object") {
          acc[key] = normalizePayloadDates(value);
        } else if (
          key.toLowerCase().includes("datetime") ||
          key.toLowerCase() === "start" ||
          key.toLowerCase() === "end"
        ) {
          acc[key] = normalizeDateTime(value);
        } else {
          acc[key] = value;
        }
        return acc;
      }, {});
    };

    let normalizedFormData = normalizePayloadDates(formData);
    if (Array.isArray(normalizedFormData)) {
      normalizedFormData = normalizedFormData.map((entry) =>
        entry &&
          typeof entry === "object" &&
          !Array.isArray(entry) &&
          isMedicationCarePlanStructure(selectedActivityStructure)
          ? mergeMedicationActivityPayloadForApi(entry)
          : entry,
      );
    } else if (isMedicationCarePlanStructure(selectedActivityStructure)) {
      normalizedFormData =
        mergeMedicationActivityPayloadForApi(normalizedFormData);
    }

    const resolvedCarePlanId = Number(
      selectedActivityStructure?.care_plan_id ||
      selectedActivityStructure?.id ||
      selectedActivityStructure?.template_id ||
      0,
    );
    if (!resolvedAdmissionId) {
      toast.error("Unable to resolve admission id for selected patient.");
      return false;
    }

    // Backend expects care_plans as a non-empty list. Multi-entry types
    // (medication/test/task) send one POST per entry so data is always one object.
    const entries = Array.isArray(normalizedFormData)
      ? normalizedFormData
      : [normalizedFormData];
    const timelineBatchId = `cp-${resolvedCarePlanId}-${Date.now()}-${Math.random()
      .toString(16)
      .slice(2, 8)}`;
    const timelineCreatedAt = new Date().toISOString();
    const timelineSortTs = Date.now();

    try {
      for (const entry of entries) {
        const entryWithBatchMeta =
          entry && typeof entry === "object"
            ? {
              ...entry,
              __timeline_group_id: timelineBatchId,
              __timeline_created_at: timelineCreatedAt,
              __timeline_sort_ts: timelineSortTs,
            }
            : entry;
        const apiPayload = {
          admission_id: resolvedAdmissionId,
          care_plans: [
            {
              care_plan_id: resolvedCarePlanId,
              data: entryWithBatchMeta,
            },
          ],
        };
        const saveResponse = await axios.post(
          `${REACT_APP_BACKEND_ROUTE_URL}care_plans/`,
          apiPayload,
          { headers },
        );
        const saveBody = saveResponse?.data || {};
        if (saveBody?.is_valid === false) {
          throw new Error(
            saveBody?.message ||
            saveBody?.detail ||
            "Care plan save was rejected by server.",
          );
        }
      }

      const localPayload = {
        type: selectedActivityStructure.name.toLowerCase().replace(/\s+/g, "-"),
        data: {
          structureId: resolvedCarePlanId,
          structureName: selectedActivityStructure.name,
          __timeline_group_id: timelineBatchId,
          __timeline_created_at: timelineCreatedAt,
          __timeline_sort_ts: timelineSortTs,
          values: Array.isArray(normalizedFormData)
            ? normalizedFormData
            : { ...normalizedFormData },
        },
      };

      onAddCarePlan(patient.id, localPayload);
      toast.success(
        `${selectedActivityStructure.name} activity saved successfully.`,
      );

      return true;
    } catch (error) {
      console.error("Failed to save care plan activity:", error);
      toast.error(
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        "Failed to save care plan activity. Please try again.",
      );
      return false;
    }
  };

  // Sanitize API response
  const sanitizeCarePlanData = (data) => {
    if (!Array.isArray(data)) {
      return [];
    }

    return data
      .filter((item) => item && typeof item === "object")
      .map((item) => {
        const inferredType = String(item.type || item.name || "")
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "-");
        const payloadData =
          item.data && typeof item.data === "object"
            ? item.data
            : item && typeof item === "object"
              ? { ...item }
              : {};

        return {
          type: inferredType,
          name: item.name || "",
          care_plan_id:
            Number(item.care_plan_id || item.carePlanId || item.id || 0) ||
            undefined,
          data: payloadData,
          id: item.id || `${Date.now()}-${Math.random().toString(16).slice(2)}`,
          createdAt: item.createdAt || new Date().toISOString(),
        };
      });
  };

  // Names a voice plan in an error toast. A rejection that just says
  // "medication" is useless when three were dictated, so lead with the drug.
  const describeVoicePlan = (cp) => {
    const med = cp?.data?.medication;
    const drug = String(med?.drug_name || med?.name || "").trim();
    return drug || String(cp?.type || "Care plan");
  };

  // Handle voice assistant flow (review + persist)
  const handleVoiceSubmit = async (payload) => {
    try {
      const VOICE_CARE_PLAN_API = `${REACT_APP_BACKEND_ROUTE_URL}add_ipd_voice_careplan/`;

      if (payload?.mode === "transcribe") {
        const { audioBlob, language } = payload;
        const formData = new FormData();
        // MediaRecorder hands us webm/opus. This used to be relabelled as
        // "voice-recording.mp3" / audio/mpeg, which is simply untrue — the
        // bytes never changed. The backend passes file.name and
        // file.content_type straight through to HTAI
        // (ipd_voice_careplan.py: files = {'file': (file.name, file,
        // file.content_type)}), so a false type is what the transcriber decodes
        // against. components/VoiceAssistant/VoiceAssistant.js already sends
        // the honest ".webm".
        const blobType = audioBlob?.type || "audio/webm";
        const extension = blobType.includes("mp4") ? "mp4" : "webm";
        const voiceFile = new File([audioBlob], `voice-recording.${extension}`, {
          type: blobType,
        });
        formData.append("file", voiceFile);
        formData.append("language", language);
        formData.append("patient_id", String(resolvedAdmissionId || 0));
        // Optional context the backend forwards when present
        // (ipd_voice_careplan.py) and the sibling OPD service already sends —
        // it lets the model sanity-check a dose against the patient.
        if (patient?.age != null && String(patient.age).trim() !== "") {
          formData.append("patient_age", String(patient.age));
        }
        if (patient?.gender != null && String(patient.gender).trim() !== "") {
          formData.append("patient_gender", String(patient.gender));
        }

        const response = await axios.post(VOICE_CARE_PLAN_API, formData, {
          headers,
        });

        const responseData = response?.data || {};
        // The proxy reports downstream failures on HTTP 200 with
        // {status:"Failed", message}. Checking only for an empty care_plans[]
        // replaced that message with a generic one, so a real cause ("Invalid
        // response from downstream service") never reached the doctor.
        if (responseData?.status === "Failed") {
          toast.error(
            responseData?.message ||
            responseData?.detail ||
            "Voice service could not process the recording.",
          );
          return { carePlans: [] };
        }

        const incomingCarePlans =
          (Array.isArray(responseData.care_plans) && responseData.care_plans) ||
          (Array.isArray(responseData.carePlans) && responseData.carePlans) ||
          (Array.isArray(responseData.data?.care_plans) &&
            responseData.data.care_plans) ||
          (Array.isArray(responseData.data?.carePlans) &&
            responseData.data.carePlans) ||
          [];

        if (
          !Array.isArray(incomingCarePlans) ||
          incomingCarePlans.length === 0
        ) {
          toast.error("No valid voice response found.");
          return { carePlans: [] };
        }

        const sanitized = sanitizeCarePlanData(incomingCarePlans).filter(
          (cp) => cp?.type !== "task-list",
        );
        if (sanitized.length === 0) {
          toast.error("No valid care plans found in the response.");
        }
        const warnings =
          (Array.isArray(responseData.data?.warnings) &&
            responseData.data.warnings) ||
          (Array.isArray(responseData.warnings) && responseData.warnings) ||
          [];
        return { carePlans: sanitized, warnings };
      }

      if (payload?.mode === "persist") {
        const carePlansToPersist = Array.isArray(payload?.carePlans)
          ? payload.carePlans
          : [];
        const voiceTimelineCreatedAt = new Date().toISOString();
        const voiceBaseSortTs = Date.now();

        if (carePlansToPersist.length === 0) {
          toast.error("No reviewed care plans to save.");
          return false;
        }

        if (!resolvedAdmissionId) {
          toast.error("Unable to resolve admission id for selected patient.");
          return false;
        }

        // Normalize every non-task-list plan regardless of whether a structure ID can be resolved.
        // plansForTimeline: all valid plans → always added to local state for the IPD timeline.
        // plansForApi: subset that has a resolvable structure ID → also POSTed to the backend.
        const plansForTimeline = [];
        const plansForApi = [];

        for (const [voiceIndex, carePlan] of carePlansToPersist.entries()) {
          const normalizedType = String(carePlan?.type || "")
            .trim()
            .toLowerCase();
          if (normalizedType === "task-list" || normalizedType === "tasklist")
            continue;

          let normalizedData =
            carePlan?.data && typeof carePlan.data === "object"
              ? carePlan.data
              : {};
          if (normalizedType === "medication") {
            normalizedData =
              mergeMedicationActivityPayloadForApi(normalizedData);
            // Belt and braces: the review step already normalised this, but a
            // row added blank from the review UI never passed through it, and a
            // medication reaching care_plans/ with period/periodUnit instead of
            // duration/duration_unit is scheduled as a one-day course.
            normalizedData = normalizeVoiceCarePlanData(normalizedData);
          }
          if (Object.keys(normalizedData).length === 0) continue;

          const resolvedCarePlanId =
            Number(carePlan?.care_plan_id || 0) ||
            resolveCarePlanStructureIdByType(carePlan.type || carePlan.name);
          const voiceTimelineGroupId = `voice-${resolvedCarePlanId || normalizedType}-${Date.now()}-${Math.random()
            .toString(16)
            .slice(2, 8)}`;
          const voiceTimelineSortTs = voiceBaseSortTs + voiceIndex;
          const normalizedDataWithTimelineMeta =
            normalizedData && typeof normalizedData === "object"
              ? {
                ...normalizedData,
                __timeline_group_id: voiceTimelineGroupId,
                __timeline_created_at: voiceTimelineCreatedAt,
                __timeline_sort_ts: voiceTimelineSortTs,
              }
              : normalizedData;

          const entry = {
            care_plan_id: resolvedCarePlanId || null,
            data: normalizedDataWithTimelineMeta,
            type: normalizedType,
          };

          plansForTimeline.push(entry);
          if (resolvedCarePlanId) plansForApi.push(entry);
        }

        if (plansForTimeline.length === 0) {
          toast.error("No valid reviewed care plans to save.");
          return false;
        }

        // Save plans with known structure IDs to the backend (same axios + headers as manual activity save).
        const persistHeaders = {
          Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}`,
          "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
          "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
          "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
          USERNAME: sessionStorage.getItem("userName") || "",
        };

        // A plan the server actually accepted. Only these may reach the
        // timeline: care_plans/ runs its medication dual-write inside
        // transaction.atomic(), so a rejected entry takes its CarePlanData row
        // down with it — showing it on the timeline would be a lie.
        const savedEntries = new Set();
        const rejectedReasons = [];

        let apiSavedCount = 0;
        let lastApiError = null;
        for (const cp of plansForApi) {
          try {
            const apiPayload = {
              admission_id: resolvedAdmissionId,
              care_plans: [
                {
                  care_plan_id: cp.care_plan_id,
                  // __billing_charge / __service_meta are internal carriers the
                  // review screen's catalogue picker writes. care_plans/ stores
                  // data verbatim, so they are stripped here exactly as the typed
                  // form's handleSubmit does — cp.data keeps them for the billing
                  // and lab dispatch that run after the save.
                  data: stripServiceMetaDeep(stripBillingMetaFromPayload(cp.data)),
                },
              ],
            };
            const saveResponse = await axios.post(
              `${REACT_APP_BACKEND_ROUTE_URL}care_plans/`,
              apiPayload,
              { headers: persistHeaders },
            );
            const saveBody = saveResponse?.data || {};
            if (saveBody?.is_valid === false) {
              throw new Error(
                saveBody?.message ||
                saveBody?.detail ||
                "Care plan save was rejected by server.",
              );
            }
            // Same 200-with-status-Failed case handleAddCarePlan already guards
            // against: the body carries no is_valid key at all when the caller
            // isn't a doctor on this admission, or the admission_id is invalid.
            if (saveBody?.status && saveBody.status !== "Success") {
              throw new Error(
                saveBody?.message || saveBody?.detail || "Care plan save failed.",
              );
            }
            // care_plans/ reports medication entries its CPOE dual-write
            // refused (unsupported frequency, missing drug name) in `skipped[]`
            // rather than as an error — "Callers MUST check this", per the
            // endpoint's own contract. Nothing was checking it, so a refused
            // medicine still produced a success toast.
            const skipped = Array.isArray(saveBody?.skipped) ? saveBody.skipped : [];
            if (skipped.length > 0) {
              skipped.forEach((entry) =>
                rejectedReasons.push(
                  `${describeVoicePlan(cp)}: ${entry?.reason || "rejected by server"}`,
                ),
              );
              continue;
            }
            savedEntries.add(cp);
            apiSavedCount++;
          } catch (err) {
            lastApiError = err;
            rejectedReasons.push(
              `${describeVoicePlan(cp)}: ${err?.response?.data?.message || err?.message || "save failed"}`,
            );
            console.error(
              "Failed to save care plan to API:",
              err?.response?.data || err?.message,
            );
          }
        }

        if (plansForApi.length > 0 && apiSavedCount === 0) {
          const status = lastApiError?.response?.status;
          if (status === 401) {
            toast.error(
              "Session expired or not authorized. Please log out and log in again, then retry.",
            );
          } else if (rejectedReasons.length > 0) {
            // Every entry was refused. Show WHY, per medicine — a generic
            // "failed to save" here is what made an unsupported frequency
            // impossible to diagnose from the screen.
            rejectedReasons.forEach((reason) => toast.error(reason));
          } else {
            toast.error(
              lastApiError?.response?.data?.message ||
              lastApiError?.response?.data?.detail ||
              lastApiError?.message ||
              "Failed to save voice care plans to the server.",
            );
          }
          // Keep the review open so the doctor can correct and retry rather
          // than losing the dictation.
          return false;
        }

        // Plans the server accepted, plus plans that were never sent because no
        // structure ID could be resolved. A REJECTED plan is deliberately
        // absent — see savedEntries above.
        const localOnly = plansForTimeline.filter((cp) => !cp.care_plan_id);
        const plansToShow = plansForTimeline.filter(
          (cp) => savedEntries.has(cp) || !cp.care_plan_id,
        );

        // Purely additive — existing manually-added care plans are never replaced.
        plansToShow.forEach((cp) => {
          const structureMeta = cp.care_plan_id
            ? carePlanStructures.find(
              (s) =>
                Number(s?.care_plan_id || s?.id || s?.template_id || 0) ===
                Number(cp.care_plan_id),
            )
            : null;
          onAddCarePlan(patient.id, {
            type: cp.type,
            data: {
              ...cp.data,
              ...(cp.care_plan_id ? { structureId: cp.care_plan_id } : {}),
              ...(structureMeta?.name
                ? { structureName: structureMeta.name }
                : {}),
            },
          });
        });

        const persistedEntries = plansForTimeline.filter((cp) => savedEntries.has(cp));

        // [BILLING] Same dispatch the typed form runs. Only entries carrying a
        // charge-master selection are billed, so this no-ops for anything the
        // doctor left as free text in the review.
        for (const cp of persistedEntries) {
          try {
            await postCarePlanBillingCharges({
              entries: [cp.data],
              admissionId: resolvedAdmissionId,
              activityName: cp.type,
              isReferredToMePatient,
              referredByProviderId: patient?.referredByProviderId || "",
              headers: persistHeaders,
            });
          } catch (billingError) {
            console.error(
              "[BILLING] Failed to post voice charge:",
              billingError?.response?.data || billingError?.message,
            );
            toast.warn(
              `${describeVoicePlan(cp)}: saved, but the charge could not be posted. Please add it from Billing.`,
            );
          }
        }

        // [LAB_TEST] No add_patient_lab_tests/ call here. care_plans/ now
        // creates the lab/radiology order itself for a dictated investigation,
        // so posting it again produced two orders per test (one LAB-/RAD- row
        // per save, visibly duplicated on Order Management).

        // Report what actually happened rather than the number of rows the
        // review screen had. This counted plansForTimeline, so a rejected or
        // never-sent plan was announced as saved.
        if (apiSavedCount > 0) {
          toast.success(`${apiSavedCount} care plan(s) saved.`);
        }
        if (localOnly.length > 0) {
          toast.warn(
            `${localOnly.length} item(s) (${localOnly
              .map((cp) => cp.type)
              .join(", ")}) could not be matched to a care plan template — shown here but NOT saved. They will disappear on refresh.`,
          );
        }
        rejectedReasons.forEach((reason) => toast.error(reason));

        return apiSavedCount > 0 || localOnly.length > 0;
      }
    } catch (error) {
      console.error("Error submitting voice recording:", error);
      toast.error("Failed to process voice recording. Please try again.");
      return false;
    }
  };

  // Check if a care plan has meaningful data
  const hasCarePlanData = (carePlan) => {
    if (!carePlan || !carePlan.data) return false;

    switch (carePlan.type) {
      case "clinical-notes":
        const { condition, goals, notes } = carePlan.data || {};
        return (
          (condition && condition.trim()) ||
          (goals && goals.trim()) ||
          (notes && notes.trim())
        );

      case "monitoring":
        const monitoringItems = carePlan.data.monitoringItems || [];
        return Array.isArray(monitoringItems) && monitoringItems.length > 0;

      case "medication":
        const medications = carePlan.data.medications || [];
        return Array.isArray(medications) && medications.length > 0;

      case "test":
        const tests = carePlan.data.tests || [];
        return Array.isArray(tests) && tests.length > 0;

      case "diet":
        const { dietType, restrictions, feedingMode, dieticianReferral } =
          carePlan.data || {};
        return (
          (dietType && dietType.trim()) ||
          (restrictions && restrictions.trim()) ||
          (feedingMode && feedingMode.trim()) ||
          (dieticianReferral && dieticianReferral.trim())
        );

      case "discharge":
        const {
          dischargePlan,
          tentativeDate,
          followUpInstructions,
          homeCareNotes,
        } = carePlan.data || {};
        return (
          (dischargePlan && dischargePlan.trim()) ||
          (tentativeDate && tentativeDate.trim()) ||
          (followUpInstructions && followUpInstructions.trim()) ||
          (homeCareNotes && homeCareNotes.trim())
        );

      case "task-list":
        const tasks = carePlan.data.tasks || [];
        return (
          Array.isArray(tasks) &&
          tasks.length > 0 &&
          tasks.some((task) => task.taskTitle && task.taskTitle.trim())
        );

      default:
        return false;
    }
  };

  // Handle wizard submission
  const handleWizardSubmit = async (carePlans) => {
    try {
      // Filter out empty care plans
      const validCarePlans = carePlans.filter((cp) => hasCarePlanData(cp));

      if (validCarePlans.length === 0) {
        toast.warning(
          "No valid care plans or tasks to add. Please add data before submitting.",
        );
        return;
      }

      // Separate care plans and tasks
      const carePlansToAdd = validCarePlans.filter(
        (cp) => cp.type !== "task-list",
      );
      const taskListPlan = validCarePlans.find((cp) => cp.type === "task-list");

      // Add care plans
      let addedCount = 0;
      for (const carePlan of carePlansToAdd) {
        await addCarePlan(patient.id, {
          type: carePlan.type,
          data: carePlan.data,
        });
        onAddCarePlan(patient.id, {
          type: carePlan.type,
          data: carePlan.data,
        });
        addedCount++;
      }

      // Add tasks if present
      let tasksAdded = 0;
      if (
        taskListPlan &&
        taskListPlan.data.tasks &&
        taskListPlan.data.tasks.length > 0
      ) {
        // Filter out tasks without taskTitle
        const validTasks = taskListPlan.data.tasks.filter(
          (task) => task.taskTitle && task.taskTitle.trim(),
        );

        if (validTasks.length > 0) {
          const currentTasks = taskPlansByPatient[patient.id] || [];
          const updatedTasks = [...currentTasks, ...validTasks];
          setTaskPlansByPatient((prev) => ({
            ...prev,
            [patient.id]: updatedTasks,
          }));
          await saveTasks(patient.id, updatedTasks);
          tasksAdded = validTasks.length;
        }
      }

      // Show success message
      const messages = [];
      if (addedCount > 0) {
        messages.push(`${addedCount} care plan(s)`);
      }
      if (tasksAdded > 0) {
        messages.push(`${tasksAdded} task(s)`);
      }
      toast.success(`${messages.join(" and ")} added successfully!`);
      setWizardData(null);
    } catch (error) {
      console.error("Error submitting care plans:", error);
      toast.error("Failed to save care plans. Please try again.");
    }
  };

  const carePlanButtons = [
    { type: "clinical-notes", label: "Clinical Notes", icon: DescriptionIcon },
    { type: "monitoring", label: "Monitoring", icon: MonitorHeartIcon },
    { type: "medication", label: "Medication", icon: MedicationIcon },
    { type: "test", label: "Test", icon: BiotechIcon },
    { type: "diet", label: "Diet", icon: RestaurantIcon },
    { type: "discharge", label: "Discharge", icon: ExitToAppIcon },
  ];

  return (
    <PanelContainer elevation={1}>
      {/* Patient Header */}
      <HeaderSection>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Avatar
            sx={{
              width: 52,
              height: 52,
              fontSize: "1rem",
              fontWeight: 700,
              backgroundColor: "#21cdc0",
              color: "#fff",
              flexShrink: 0,
            }}
          >
            {getInitials(patient.name)}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1rem",
                color: "#121959",
                lineHeight: 1.3,
              }}
            >
              {patient.name}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mt: 0.3,
                flexWrap: "wrap",
              }}
            >
              {(patient.patient_id || patient.pat_id) && (
                <Typography
                  sx={{ fontSize: "0.72rem", color: "text.secondary" }}
                >
                  {patient.patient_id || patient.pat_id}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.6,
                mt: 0.5,
                flexWrap: "wrap",
              }}
            >
              {patient.age && (
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    color: "#374151",
                    fontWeight: 500,
                  }}
                >
                  {patient.age} Y
                </Typography>
              )}
              {patient.age && patient.gender && (
                <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af" }}>
                  |
                </Typography>
              )}
              {patient.gender && (
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    color: "#374151",
                    fontWeight: 500,
                  }}
                >
                  {patient.gender}
                </Typography>
              )}
              {(patient.age || patient.gender) && patient.bloodGroup && (
                <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af" }}>
                  |
                </Typography>
              )}
              {patient.bloodGroup && (
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    color: "#374151",
                    fontWeight: 500,
                  }}
                >
                  {patient.bloodGroup}
                </Typography>
              )}
              {patient.bed_type_name && (
                <Chip
                  label={patient.bed_type_name}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: "0.62rem",
                    height: 20,
                    color: "#16a34a",
                    borderColor: "#16a34a",
                    fontWeight: 600,
                    ml: 0.5,
                  }}
                />
              )}
            </Box>
          </Box>

          {onClose && (
            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                mt: -0.5,
                color: "#9ca3af",
                "&:hover": { color: "#374151" },
              }}
            >
              <CloseIcon sx={{ fontSize: "1.1rem" }} />
            </IconButton>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mt: 1.2,
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ fontSize: "0.73rem", color: "text.secondary" }}>
            Bed:
          </Typography>
          <Typography
            sx={{ fontSize: "0.73rem", color: "#374151", fontWeight: 600 }}
          >
            {patient.bedNo || "-"}
          </Typography>
          <Typography sx={{ fontSize: "0.73rem", color: "#d1d5db", mx: 0.3 }}>
            •
          </Typography>
          <Typography sx={{ fontSize: "0.73rem", color: "text.secondary" }}>
            Ward:
          </Typography>
          <Typography
            sx={{ fontSize: "0.73rem", color: "#374151", fontWeight: 600 }}
          >
            {patient.bed_type_name || "-"}
          </Typography>
          <Typography sx={{ fontSize: "0.73rem", color: "#d1d5db", mx: 0.3 }}>
            •
          </Typography>
          <Typography sx={{ fontSize: "0.73rem", color: "text.secondary" }}>
            Location:
          </Typography>
          <Typography
            sx={{ fontSize: "0.73rem", color: "#374151", fontWeight: 600 }}
          >
            {patient.location || "-"}
          </Typography>
        </Box>
      </HeaderSection>

      {/* Vitals Section - Compact */}
      <Box sx={{ p: 2, pb: 1 }}>
        <Accordion
          defaultExpanded={false}
          elevation={0}
          disableGutters
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ minHeight: 44 }}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Current Vitals
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: "auto" }}
              >
                Updated: {patientVitals.lastUpdated}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <VitalsPanel vitals={patientVitals} showHeader={false} />
            {/* Nurse risk assessment — read-only (risk stays nurse-owned). */}
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                Risk Assessment (recorded by nursing)
              </Typography>
              {riskAssessment ? (
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                  <Chip
                    size="small"
                    label={`Fall: ${riskAssessment.fall_risk || "—"}`}
                    color={riskColor(riskAssessment.fall_risk)}
                  />
                  <Chip
                    size="small"
                    label={`Pressure Ulcer: ${riskAssessment.pressure_ulcer_risk || "—"}`}
                    color={riskColor(riskAssessment.pressure_ulcer_risk)}
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={`Pain ${riskAssessment.pain_score ?? "—"}/10`}
                    variant="outlined"
                  />
                  {riskAssessment.allergies && (
                    <Chip
                      size="small"
                      label={`Allergies: ${riskAssessment.allergies}`}
                      color="error"
                      variant="outlined"
                    />
                  )}
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No assessment recorded.
                </Typography>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>

      <Divider />

      {/* Action Buttons & Care Plans */}
      <Box sx={{ p: 2, flex: 1, overflowY: "auto" }}>
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AccessTimeIcon />}
            onClick={() => setActiveModal("timeline")}
            size="small"
            sx={{ flex: 1 }}
          >
            IPD Timeline
          </Button>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => setActiveModal("patient-360")}
            size="small"
            sx={{ flex: 1 }}
          >
            Patient 360
          </Button>
          <Button
            variant="outlined"
            startIcon={<SwapHorizIcon />}
            onClick={openTransferCare}
            size="small"
            sx={{ flex: 1 }}
          >
            Transfer Care
          </Button>
        </Box>

        {/* Transfer Care dialog (nursing-ops-2 #09): pick the new attending;
            they must accept from Referred To Me before the attending flips.
            The patient's bed is untouched in every case. */}
        <Dialog open={transferCareOpen} onClose={() => setTransferCareOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Transfer Care</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2, fontSize: 13 }}>
              Propose a new attending doctor. Nothing changes until they accept
              from their "Referred To Me" list; the patient keeps their bed.
            </DialogContentText>
            <Autocomplete
              size="small"
              options={transferDoctors}
              value={transferTarget}
              onChange={(_, v) => setTransferTarget(v)}
              getOptionLabel={(d) =>
                d ? `${d.name || d.doctor_name || d.doctor_id}${d.department ? ` — ${d.department}` : ""}` : ""
              }
              isOptionEqualToValue={(o, v) => (o.doctor_id || o.id) === (v.doctor_id || v.id)}
              renderInput={(params) => <TextField {...params} label="New attending doctor" />}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Note (optional)"
              value={transferNote}
              onChange={(e) => setTransferNote(e.target.value)}
              multiline
              minRows={2}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTransferCareOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!transferTarget || transferBusy}
              onClick={submitTransferCare}
            >
              {transferBusy ? "Proposing..." : "Propose Transfer"}
            </Button>
          </DialogActions>
        </Dialog>
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            fullWidth
            startIcon={<AssignmentIcon />}
            onClick={() => setOrderMgmtOpen(true)}
            size="small"
            sx={{
              backgroundColor: "#0E3F66",
              "&:hover": { backgroundColor: "#0a2e4d" },
              textTransform: "none",
              fontWeight: 700,
              letterSpacing: 0.5,
            }}
          >
            ORDER MGMT
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Care Plans Section - Accordion like Vitals */}
        {/* <Box sx={{ pb: 1 }}>
          <Accordion
            defaultExpanded={false}
            elevation={0}
            disableGutters
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ minHeight: 44 }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Make Care Plans
                </Typography>
                {patientCarePlans.length > 0 && (
                  <Chip
                    label={patientCarePlans.length}
                    size="small"
                    color="primary"
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Grid container spacing={1}>
                {carePlanButtons.map(({ type, label, icon: Icon }) => (
                  <Grid item xs={6} key={type}>
                    <CarePlanButton
                      variant="contained"
                      fullWidth
                      onClick={() => setActiveModal(type)}
                    >
                      <Icon />
                      <Typography variant="caption">{label}</Typography>
                    </CarePlanButton>
                  </Grid>
                ))}
              </Grid>
              {patientCarePlans.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  {patientCarePlans.length} care plan(s) added
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        </Box> */}

        {/* Care Plan Activities Section */}
        <Box sx={{ pb: 1, opacity: dischargeOnlyActionsDisabled ? 0.5 : 1 }}>
          <Accordion
            defaultExpanded={false}
            disabled={dischargeOnlyActionsDisabled}
            elevation={0}
            disableGutters
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ minHeight: 44 }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Care Plan Activities
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <CarePlanButton
                    variant="contained"
                    fullWidth
                    disabled={dischargeOnlyActionsDisabled}
                    onClick={() => setActiveModal("discharge-advice")}
                  >
                    <ExitToAppIcon />
                    <Typography variant="caption">Discharge Advice</Typography>
                  </CarePlanButton>
                </Grid>
                {regularCarePlanStructures.map((activity) => (
                  <Grid item xs={6} key={activity.id}>
                    <CarePlanButton
                      variant="contained"
                      fullWidth
                      disabled={dischargeOnlyActionsDisabled}
                      onClick={() => setSelectedActivityStructure(activity)}
                    >
                      <DescriptionIcon />
                      <Typography variant="caption">{activity.name}</Typography>
                    </CarePlanButton>
                  </Grid>
                ))}
              </Grid>
              {loadingCarePlanStructures ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1 }}
                >
                  Loading care plan activities...
                </Typography>
              ) : regularCarePlanStructures.length === 0 ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1 }}
                >
                  No care plan activities available.
                </Typography>
              ) : null}
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Medication Orders (CPOE order book) — status per medicine + doctor's Cancel */}
        <MedicationOrdersPanel
          admissionId={resolvedAdmissionId}
          disabled={dischargeOnlyActionsDisabled}
        />

        {/* Operation Theatre — the doctor's provisional booking.

            An inpatient is the COMMON case for surgery, not the exception, and
            until this existed the request form lived only in the OPD console:
            the surgeon of an admitted patient had no way to ask for theatre at
            all. The same component is used, so the two consoles cannot drift.

            The admission is not passed. OT resolves it from the patient at
            SCHEDULING time (`open_admission_for`), which is right: a request
            raised today may be booked after the patient has been moved, and the
            admission that matters is the one they are in when they go down. */}
        <Box sx={{ pb: 1, opacity: dischargeOnlyActionsDisabled ? 0.5 : 1 }}>
          <Accordion
            defaultExpanded={false}
            disabled={dischargeOnlyActionsDisabled}
            elevation={0}
            disableGutters
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44 }}>
              <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1 }}>
                <LocalHospitalIcon sx={{ fontSize: 18, color: "#21cdc0" }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Operation Theatre
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                  Ask theatre for a slot
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <OtRequestSection
                patientId={
                  patient?.patient_id || patient?.pat_id || patient?.patientId || ""
                }
                doctorData={{
                  doctor_id: sessionStorage.getItem("doctor_id") || "",
                  name:
                    patient?.doctorName ||
                    patient?.doctor_name ||
                    sessionStorage.getItem("userName") ||
                    "",
                }}
                // The working diagnosis travels with the request so theatre
                // never asks a ward to repeat what is already on the admission.
                diagnosisText={
                  patient?.finalDiagnosis ||
                  patient?.provisionalDiagnosis ||
                  patient?.condition ||
                  ""
                }
              />
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Referral Doctors — hidden for Referred To Me patients */}
        {!isReferredToMePatient && (
          <Box sx={{ pb: 1, opacity: dischargeOnlyActionsDisabled ? 0.5 : 1 }}>
            <Accordion
              defaultExpanded={false}
              disabled={dischargeOnlyActionsDisabled}
              elevation={0}
              disableGutters
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ minHeight: 44 }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <MedicalServicesIcon
                    sx={{ color: "#00BFA5", fontSize: 20 }}
                  />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Referral Doctors
                  </Typography>
                  {referredDoctors.length > 0 && (
                    <Chip
                      label={referredDoctors.length}
                      size="small"
                      sx={{
                        backgroundColor: "#e0f2f1",
                        color: "#00796b",
                        fontWeight: 600,
                        height: 20,
                        fontSize: "0.75rem",
                      }}
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {referredDoctors.map((doc) => (
                    <Box
                      key={doc.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1.5,
                        backgroundColor: "#fff",
                        transition: "box-shadow 0.2s, border-color 0.2s",
                        "&:hover": {
                          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                          borderColor: "primary.light",
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          backgroundColor: "#f5f5f5",
                          color: "#616161",
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        {doc.initials}
                      </Avatar>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "text.primary",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {doc.name}
                          </Typography>
                        </Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", fontSize: "0.75rem" }}
                        >
                          {doc.specialty}
                        </Typography>
                      </Box>

                      <Button
                        size="small"
                        disabled={dischargeOnlyActionsDisabled}
                        onClick={() => handlePromptRemoveReferredDoctor(doc)}
                        sx={{
                          minWidth: 0,
                          p: 0.5,
                          color: "text.secondary",
                          borderRadius: "50%",
                          "&:hover": {
                            backgroundColor: "#ffebee",
                            color: "error.main",
                          },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </Button>
                    </Box>
                  ))}

                  {referredDoctors.length === 0 && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: "center", py: 2, fontStyle: "italic" }}
                    >
                      No referral doctors assigned
                    </Typography>
                  )}

                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    disabled={dischargeOnlyActionsDisabled}
                    onClick={handleOpenAddReferredDoctorDialog}
                    fullWidth
                    sx={{
                      mt: 1,
                      textTransform: "none",
                      fontWeight: 600,
                      borderColor: "#b2dfdb",
                      color: "#00796b",
                      "&:hover": {
                        borderColor: "#00796b",
                        backgroundColor: "#e0f2f1",
                      },
                    }}
                  >
                    Add Referral Doctor
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}

        {/* Nursing Notes (Epic 9, D11) — read-only. Notes are authored by
            nurses via the Nurse Dashboard; nothing here can create/edit/delete
            them. */}
        <Box sx={{ pb: 1 }}>
          <Accordion
            defaultExpanded={false}
            elevation={0}
            disableGutters
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ minHeight: 44 }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <EventNoteIcon sx={{ color: "#7b1fa2", fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Nursing Notes
                </Typography>
                {nursingNotes.length > 0 && (
                  <Chip
                    label={nursingNotes.length}
                    size="small"
                    sx={{
                      backgroundColor: "#f3e5f5",
                      color: "#7b1fa2",
                      fontWeight: 600,
                      height: 20,
                      fontSize: "0.75rem",
                    }}
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
              {nursingNotesLoading ? (
                <Typography variant="caption" color="text.secondary">
                  Loading nursing notes...
                </Typography>
              ) : nursingNotesError ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center", py: 2, fontStyle: "italic" }}
                >
                  Couldn't load nursing notes.
                </Typography>
              ) : nursingNotes.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center", py: 2, fontStyle: "italic" }}
                >
                  No nursing notes recorded
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {nursingNotes.map((note) => (
                    <Box
                      key={note.id}
                      sx={{
                        p: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1.5,
                        backgroundColor: "#fff",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "text.primary" }}
                        >
                          {note.author_name || note.author || "Unknown"}
                        </Typography>
                        {note.note_type && (
                          <Chip
                            label={note.note_type}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: "0.7rem" }}
                          />
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 0.5 }}
                      >
                        {formatIstDateTime(note.recorded_at, "-")}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-wrap", color: "text.primary" }}
                      >
                        {note.content}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Task Plans Button */}
        {/* <Button
          variant="outlined"
          startIcon={<AssignmentIcon />}
          onClick={() => setActiveModal("task-plans")}
          size="small"
          fullWidth
          sx={{ mt: 1.5 }}
        >
          Task Plans
        </Button> */}

        {/* Voice Assistant Button */}
        <Button
          variant="outlined"
          startIcon={<MicIcon />}
          disabled={dischargeOnlyActionsDisabled}
          onClick={() => setActiveModal("voice-assistant")}
          size="small"
          fullWidth
          sx={{ mt: 1.5 }}
        >
          Voice Assistant
        </Button>

        {!isReferredToMePatient && (
          <>
            {dischargeInitiated && (
              <Box
                sx={{
                  mt: 1.5,
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <DischargeStageTracker
                  admissionId={resolvedAdmissionId}
                  onAdvanced={(updated) =>
                    setDischargeProcess((prev) => updated || prev)
                  }
                  onOpenSummary={() => setActiveModal("discharge-summary")}
                />
                <Divider sx={{ my: 2 }} />
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CloseIcon />}
                  onClick={() => setActiveModal("cancel-discharge")}
                  size="small"
                  fullWidth
                >
                  Cancel Discharge
                </Button>
              </Box>
            )}

            <Button
              variant="outlined"
              startIcon={<DescriptionIcon />}
              disabled={dischargeSummaryDisabled}
              onClick={() => setActiveModal("discharge-summary")}
              size="small"
              fullWidth
              sx={{ mt: 1.5 }}
            >
              Discharge Summary
            </Button>

            <Button
              variant="outlined"
              startIcon={<DescriptionIcon />}
              disabled={dischargeSummaryDisabled || !resolvedAdmissionId}
              onClick={() => setActiveModal("view-discharge-draft")}
              size="small"
              fullWidth
              sx={{ mt: 1 }}
            >
              View Saved Draft
            </Button>

            {admittingDoctorInfo?.name && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1.5 }}
              >
                Admitting Doctor: {admittingDoctorInfo.name}
              </Typography>
            )}
            {isCurrentDoctorAdmitting(admittingDoctorInfo?.id) && (
              <Button
                variant="outlined"
                startIcon={<SwapHorizIcon />}
                disabled={dischargeOnlyActionsDisabled}
                onClick={() => setActiveModal("handover-doctor")}
                size="small"
                fullWidth
                sx={{ mt: 1 }}
              >
                Handover Patient
              </Button>
            )}
          </>
        )}
      </Box>

      {/* Modals */}
      <ClinicalNotesModal
        open={activeModal === "clinical-notes"}
        onClose={() => setActiveModal(null)}
        onSubmit={(data, options) =>
          handleAddCarePlan("clinical-notes", data, options)
        }
        structure={(() => {
          const match = carePlanStructures.find((s) => {
            const n = String(s?.name || "")
              .toLowerCase()
              .replace(/[\s_-]/g, "");
            return n.includes("clinicalnote") || n.includes("clinicalnotes");
          });
          return Array.isArray(match?.structure) ? match.structure : [];
        })()}
      />
      <MonitoringModal
        open={activeModal === "monitoring"}
        onClose={() => setActiveModal(null)}
        onSubmit={(data, options) =>
          handleAddCarePlan("monitoring", data, options)
        }
      />
      <MedicationModal
        open={activeModal === "medication"}
        onClose={() => setActiveModal(null)}
        onSubmit={(data, options) =>
          handleAddCarePlan("medication", data, options)
        }
      />
      <TestModal
        open={activeModal === "test"}
        onClose={() => setActiveModal(null)}
        onSubmit={(data, options) => handleAddCarePlan("test", data, options)}
      />
      <DietModal
        open={activeModal === "diet"}
        onClose={() => setActiveModal(null)}
        onSubmit={(data, options) => handleAddCarePlan("diet", data, options)}
      />
      <DischargeAdviceModal
        open={activeModal === "discharge-advice"}
        onClose={() => setActiveModal(null)}
        onSubmit={(data, options) =>
          handleAddCarePlan("Discharge Advice", data, options)
        }
      />
      <PatientTimelineModal
        open={activeModal === "timeline"}
        onClose={() => setActiveModal(null)}
        patientId={patient.id}
        admissionId={resolvedAdmissionId}
        patientName={patient.name}
        carePlans={patientCarePlans}
        carePlanStructures={carePlanStructures}
        onStopMedication={onStopMedication}
      />
      <Patient360Modal
        open={activeModal === "patient-360"}
        handleClose={() => setActiveModal(null)}
        patientSummary={patientSummary?.medical_summary}
        isLoadingSummary={isLoadingSummary}
        totalRecords={patientSummary?.total_records || 0}
        orgCode={sessionStorage.getItem("ORG_CODE") || ""}
        patientId={resolvedPatientId}
        abdmData={abdmData}
        patientData={patient || {}}
        s3BucketFhir={s3BucketFhir}
        care_context_list={careContextList}
        selectedContextForRetrieveFhir={selectedContextForRetrieveFhir}
        handleFhirArray={handleFhirArray}
        isAbdmEnabled={isAbdmEnabledForIpd}
        isAiEnabled={isAiEnabledForIpd}
      />
      <TaskPlansModal
        open={activeModal === "task-plans"}
        onClose={() => setActiveModal(null)}
        patientId={patient.id}
        patientName={patient.name}
        initialTasks={taskPlansByPatient[patient.id] || []}
        onSaveTasks={handleSaveTasks}
      />
      <VoiceAssistantModal
        open={activeModal === "voice-assistant"}
        onClose={() => setActiveModal(null)}
        onSubmit={handleVoiceSubmit}
        patientId={patient.id}
        admissionId={resolvedAdmissionId}
        carePlanStructures={carePlanStructures}
      />
      <DischargeSummaryModal
        open={!isReferredToMePatient && activeModal === "discharge-summary"}
        onClose={() => setActiveModal(null)}
        admissionId={resolvedAdmissionId}
        patientName={patient.name}
        patientAge={patient.age}
        patientGender={patient.gender}
        patientBedNo={patient.bedNo}
        authHeaders={headers}
        admittingDoctorId={admittingDoctorInfo?.id}
      />
      <ViewDischargeDraftModal
        open={!isReferredToMePatient && activeModal === "view-discharge-draft"}
        onClose={() => setActiveModal(null)}
        admissionId={resolvedAdmissionId}
        authHeaders={headers}
      />
      <CancelDischargeModal
        open={activeModal === "cancel-discharge"}
        onClose={() => setActiveModal(null)}
        dischargeProcessId={dischargeProcess?.id}
        admittingDoctorId={admittingDoctorInfo?.id}
        onCancelled={(updated) =>
          setDischargeProcess((prev) => updated || prev)
        }
      />
      <CarePlanWizard
        open={activeModal === "wizard"}
        onClose={() => {
          setActiveModal(null);
          setWizardData(null);
        }}
        onSubmit={handleWizardSubmit}
        carePlansData={wizardData || []}
        patientId={patient.id}
        patientName={patient.name}
      />
      {/* [BILLING] admissionId + payout fields; [LAB_TEST] careContextId/doctorName from ipd_admission list; [PHARMACY_RX] patientId/diagnosis/allergies for add_ipd_prescription/ */}
      <CarePlanActivityModal
        open={Boolean(selectedActivityStructure)}
        onClose={() => setSelectedActivityStructure(null)}
        structureDef={selectedActivityStructure}
        onSubmit={handleCarePlanActivitySubmit}
        admissionId={resolvedAdmissionId}
        careContextId={
          patient?.careContextRef || patient?.care_context_ref || ""
        }
        doctorName={patient?.doctorName || patient?.doctor_name || ""}
        isReferredToMePatient={isReferredToMePatient}
        referredByProviderId={patient?.referredByProviderId || ""}
        patientId={patient?.patient_id || patient?.pat_id || patient?.patientId || ""}
        provisionalDiagnosis={patient?.provisionalDiagnosis || patient?.condition || ""}
        finalDiagnosis={patient?.finalDiagnosis || patient?.condition || ""}
        allergies={patient?.allergies || []}
      />
      {!isReferredToMePatient && (
        <>
          {/* Remove Referral Doctor Confirmation Dialog */}
          <Dialog
            open={removeReferredDoctorConfirmOpen}
            onClose={() => setRemoveReferredDoctorConfirmOpen(false)}
            aria-labelledby="remove-doctor-dialog-title"
            aria-describedby="remove-doctor-dialog-description"
            PaperProps={{
              sx: {
                borderRadius: 3,
                p: 1.5,
                maxWidth: 440,
              },
            }}
          >
            <DialogTitle
              id="remove-doctor-dialog-title"
              sx={{
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "error.main",
              }}
            >
              Confirm Removal
            </DialogTitle>
            <DialogContent>
              <DialogContentText
                id="remove-doctor-dialog-description"
                sx={{ color: "text.primary", fontSize: "0.95rem" }}
              >
                Are you sure you want to remove{" "}
                <strong>{referredDoctorToRemove?.name}</strong> from this
                patient's list of referral doctors?
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 1, gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => setRemoveReferredDoctorConfirmOpen(false)}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  color: "text.secondary",
                  borderColor: "divider",
                  "&:hover": {
                    borderColor: "text.secondary",
                    backgroundColor: "action.hover",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleConfirmRemoveReferredDoctor}
                autoFocus
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: "none",
                  "&:hover": { boxShadow: "none" },
                }}
              >
                Yes, Remove
              </Button>
            </DialogActions>
          </Dialog>

          {/* Add Referral Doctor Dialog */}
          <Dialog
            open={addReferredDoctorDialogOpen}
            onClose={handleAddReferredDoctorDialogClose}
            TransitionProps={{ onExited: resetAddReferredDoctorForm }}
            aria-labelledby="add-doctor-dialog-title"
            PaperProps={{
              sx: {
                borderRadius: 3,
                p: 1.5,
                width: "100%",
                maxWidth: 460,
              },
            }}
          >
            <DialogTitle
              id="add-doctor-dialog-title"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              Add Referral Doctor
            </DialogTitle>
            <DialogContent sx={{ pt: 1.5 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                  mt: 1,
                }}
              >
                <FormControl fullWidth size="small">
                  <InputLabel id="select-dept-label" shrink>
                    Department
                  </InputLabel>
                  <Select
                    labelId="select-dept-label"
                    value={selectedDepartment}
                    label="Department"
                    displayEmpty
                    renderValue={(selected) => selected || ""}
                    onChange={(e) => handleDepartmentChange(e.target.value)}
                    disabled={loadingDepts}
                  >
                    {departments.map((dept) => (
                      <MenuItem key={dept} value={dept}>
                        {dept}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel id="select-doctor-label" shrink>
                    Doctor
                  </InputLabel>
                  <Select
                    labelId="select-doctor-label"
                    id="select-doctor"
                    value={selectedDoctorId}
                    label="Doctor"
                    displayEmpty
                    renderValue={(selected) => {
                      if (loadingDoctors) return "Loading doctors...";
                      if (!selected) return "";
                      const doc = availableDoctors.find(
                        (d) => String(d.doctor_id) === String(selected),
                      );
                      return doc?.name || "";
                    }}
                    onChange={(e) =>
                      setSelectedDoctorId(String(e.target.value))
                    }
                    disabled={!selectedDepartment || loadingDoctors}
                  >
                    {availableDoctors
                      .filter(
                        (doc) =>
                          !referredDoctors.some(
                            (assigned) =>
                              String(assigned.id) === String(doc.doctor_id),
                          ) &&
                          String(doc.doctor_id) !==
                          String(sessionStorage.getItem("doctor_id")),
                      )
                      .map((doc) => (
                        <MenuItem
                          key={doc.doctor_id}
                          value={String(doc.doctor_id)}
                        >
                          <Box
                            sx={{ display: "flex", flexDirection: "column" }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {doc.name}
                            </Typography>
                            {doc.specialty && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {doc.specialty}
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  size="small"
                  label="Notes"
                  placeholder="Add referral notes (optional)"
                  value={addReferralDoctorNotes}
                  onChange={(e) => setAddReferralDoctorNotes(e.target.value)}
                  multiline
                  minRows={3}
                  maxRows={6}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 1, gap: 1 }}>
              <Button
                variant="outlined"
                onClick={handleAddReferredDoctorDialogClose}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  color: "text.secondary",
                  borderColor: "divider",
                  "&:hover": {
                    borderColor: "text.secondary",
                    backgroundColor: "action.hover",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleAddReferredDoctor}
                disabled={!selectedDoctorId}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  backgroundColor: "#00796b",
                  boxShadow: "none",
                  "&:hover": { backgroundColor: "#004d40", boxShadow: "none" },
                }}
              >
                Add Doctor
              </Button>
            </DialogActions>
          </Dialog>

          {/* Handover Patient Dialog */}
          <Dialog
            open={activeModal === "handover-doctor"}
            onClose={() => {
              if (!submittingHandover) setActiveModal(null);
            }}
            TransitionProps={{ onExited: resetHandoverForm }}
            aria-labelledby="handover-doctor-dialog-title"
            PaperProps={{
              sx: {
                borderRadius: 3,
                p: 1.5,
                width: "100%",
                maxWidth: 460,
              },
            }}
          >
            <DialogTitle
              id="handover-doctor-dialog-title"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              Handover Patient
            </DialogTitle>
            <DialogContent sx={{ pt: 1.5 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                  mt: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Transfer the admitting-doctor role for this patient to
                  another doctor. You will be demoted to an attending doctor
                  on this case.
                </Typography>

                <FormControl fullWidth size="small">
                  <InputLabel id="handover-select-dept-label" shrink>
                    Department
                  </InputLabel>
                  <Select
                    labelId="handover-select-dept-label"
                    value={handoverDepartment}
                    label="Department"
                    displayEmpty
                    renderValue={(selected) => selected || ""}
                    onChange={(e) =>
                      handleHandoverDepartmentChange(e.target.value)
                    }
                    disabled={loadingHandoverDepts}
                  >
                    {handoverDepartments.map((dept) => (
                      <MenuItem key={dept} value={dept}>
                        {dept}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel id="handover-select-doctor-label" shrink>
                    New Admitting Doctor
                  </InputLabel>
                  <Select
                    labelId="handover-select-doctor-label"
                    id="handover-select-doctor"
                    value={handoverDoctorId}
                    label="New Admitting Doctor"
                    displayEmpty
                    renderValue={(selected) => {
                      if (loadingHandoverDoctors) return "Loading doctors...";
                      if (!selected) return "";
                      const doc = handoverDoctors.find(
                        (d) => String(d.doctor_id) === String(selected),
                      );
                      return doc?.name || "";
                    }}
                    onChange={(e) =>
                      setHandoverDoctorId(String(e.target.value))
                    }
                    disabled={!handoverDepartment || loadingHandoverDoctors}
                  >
                    {handoverDoctors
                      .filter(
                        (doc) =>
                          String(doc.doctor_id) !==
                          String(sessionStorage.getItem("doctor_id")),
                      )
                      .map((doc) => (
                        <MenuItem
                          key={doc.doctor_id}
                          value={String(doc.doctor_id)}
                        >
                          <Box
                            sx={{ display: "flex", flexDirection: "column" }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {doc.name}
                            </Typography>
                            {doc.specialization && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {doc.specialization}
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 1, gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => setActiveModal(null)}
                disabled={submittingHandover}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  color: "text.secondary",
                  borderColor: "divider",
                  "&:hover": {
                    borderColor: "text.secondary",
                    backgroundColor: "action.hover",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleHandoverPatient}
                disabled={!handoverDoctorId || submittingHandover}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  backgroundColor: "#0E3F66",
                  boxShadow: "none",
                  "&:hover": { backgroundColor: "#0a2e4d", boxShadow: "none" },
                }}
              >
                {submittingHandover ? "Transferring..." : "Handover"}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
      {/* Order Management Full-Screen Drawer — position:fixed so it covers the viewport */}
      {orderMgmtOpen && (
        <OrderManagementDrawer
          open={orderMgmtOpen}
          onClose={() => setOrderMgmtOpen(false)}
          patient={patient}
          onStopMedication={onStopMedication}
        />
      )}
    </PanelContainer>
  );
};

export default PatientDetailsPanel;
